import { Component, OnInit } from "@angular/core";
import { AjaxService } from "src/app/service/ajax.service";
import { UtilService } from "src/app/service/util.service";
import { API } from "src/environments/environment";

export interface DiarioRespostaLinha {
  id: number;
  nomeResponsavel: string;
  nomeCrianca: string;
  sexoCrianca: string;
  tituloAtividadePrograma: string;
  tituloEtapaPrograma: string;
  textoEscolhido: string;
  opcaoEscolhido: string;
  path: string;
  /** CAMPO_LIVRE (ex.: alimento). */
  alimento: string;
  observacao: string;
  dataCadastro: string;
  /** Epoch ms para ordenação (evita ambiguidade de Date.parse com dd/MM/yyyy). */
  dataCadastroOrdem: number;
}

export interface ResponsavelOpcao {
  id: number;
  nome: string;
}

export interface ProgramaOpcao {
  id: number;
  nome: string;
}

@Component({
  selector: "app-visualizar-diario-respostas",
  templateUrl: "./visualizar-diario-respostas.component.html",
  styleUrls: ["./visualizar-diario-respostas.component.scss"],
})
export class VisualizarDiarioRespostasComponent implements OnInit {
  linhas: DiarioRespostaLinha[] = [];
  filtradas: DiarioRespostaLinha[] = [];
  responsaveis: ResponsavelOpcao[] = [];
  programas: ProgramaOpcao[] = [];
  /** null = todos */
  idResponsavelFiltro: number | null = null;
  /** null = nenhum (obrigatório escolher programa antes de carregar dados). */
  idProgramaFiltro: number | null = null;
  searchText = "";
  limiteCarregar = 8000;

  page = 1;
  pageSize = 15;
  readonly opcoesTamanhoPagina = [10, 15, 25, 50, 100];

  constructor(private ajax: AjaxService, public util: UtilService) {}

  ngOnInit(): void {
    this.carregarProgramas();
  }

  get linhasPagina(): DiarioRespostaLinha[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filtradas.slice(start, start + this.pageSize);
  }

  get indiceInicio(): number {
    if (!this.filtradas.length) {
      return 0;
    }
    return (this.page - 1) * this.pageSize + 1;
  }

  get indiceFim(): number {
    return Math.min(this.page * this.pageSize, this.filtradas.length);
  }

  carregarResponsaveisPorPrograma(): void {
    const idPrograma = this.idProgramaFiltro;
    if (idPrograma == null) {
      this.responsaveis = [];
      return;
    }
    const url =
      API +
      "Diario/Diario/listarResponsaveisDiarioPorPrograma?idPrograma=" +
      idPrograma;
    this.ajax.get(url).subscribe(
      (response) => {
        const lista = response?.data || [];
        this.responsaveis = lista
          .map((x: any) => ({
            id: Number(
              x.iD_RESPONSAVEL_TEA ?? x.ID_RESPONSAVEL_TEA ?? x.id ?? 0
            ),
            nome: this.extrairNomeResponsavelOpcaoDiario(x),
          }))
          .filter((r: ResponsavelOpcao) => r.id > 0)
          .sort((a: ResponsavelOpcao, b: ResponsavelOpcao) =>
            a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
          );
      },
      () => {
        this.responsaveis = [];
        this.util.toasterShowError(
          "Atenção",
          "Erro ao carregar responsáveis deste programa"
        );
      }
    );
  }

  /** Resposta do endpoint listarResponsaveisDiarioPorPrograma (NOME / nome). */
  private extrairNomeResponsavelOpcaoDiario(x: any): string {
    const nome = String(x.nome ?? x.NOME ?? "").trim();
    if (nome) {
      return nome;
    }
    const id = Number(x.iD_RESPONSAVEL_TEA ?? x.ID_RESPONSAVEL_TEA ?? 0);
    return id > 0 ? `Responsável #${id}` : "Sem nome";
  }

  onResponsavelFiltroChange(): void {
    this.page = 1;
    this.carregar();
  }

  carregarProgramas(): void {
    const url = API + "Programa/Programa?STATUS=true";
    this.ajax.get(url).subscribe(
      (response) => {
        const lista = response?.data || [];
        this.programas = lista
          .map((x: any) => ({
            id: Number(x.iD_PROGRAMA ?? x.ID_PROGRAMA ?? x.id ?? 0),
            nome: this.extrairDescricaoPrograma(x),
          }))
          .filter((p: ProgramaOpcao) => p.id > 0)
          .sort((a: ProgramaOpcao, b: ProgramaOpcao) =>
            a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
          );
      },
      () => {
        this.util.toasterShowError(
          "Atenção",
          "Erro ao carregar lista de programas"
        );
      }
    );
  }

  private extrairDescricaoPrograma(x: any): string {
    const nome = String(x.descricao ?? x.DESCRICAO ?? "").trim();
    if (nome) {
      return nome;
    }
    const id = Number(x.iD_PROGRAMA ?? x.ID_PROGRAMA ?? 0);
    return id > 0 ? `Programa #${id}` : "Sem nome";
  }

  onProgramaFiltroChange(): void {
    this.page = 1;
    this.searchText = "";
    this.idResponsavelFiltro = null;
    this.responsaveis = [];
    if (this.idProgramaFiltro == null) {
      this.linhas = [];
      this.filtradas = [];
      this.ajustarPagina();
      return;
    }
    this.carregarResponsaveisPorPrograma();
    this.carregar();
  }

  private montarUrlListaDiario(): string {
    let url =
      API +
      "Diario/Diario/listarDiarioRespostasAdministracao?limite=" +
      this.limiteCarregar;
    if (this.idResponsavelFiltro != null) {
      url += "&idResponsavelTea=" + this.idResponsavelFiltro;
    }
    if (this.idProgramaFiltro != null) {
      url += "&idPrograma=" + this.idProgramaFiltro;
    }
    return url;
  }

  atualizarDados(): void {
    if (this.idProgramaFiltro != null) {
      this.carregarResponsaveisPorPrograma();
      this.carregar();
    } else {
      this.carregarProgramas();
    }
  }

  carregar(): void {
    if (this.idProgramaFiltro == null) {
      this.linhas = [];
      this.filtradas = [];
      this.page = 1;
      this.ajustarPagina();
      return;
    }
    this.util.showLoading();
    const url = this.montarUrlListaDiario();
    this.ajax.get(url).subscribe(
      (response) => {
        const lista = response?.data || [];
        this.linhas = this.ordenarLinhasPorDataCrescente(
          lista.map((item: any) => this.mapLinha(item))
        );
        const termo = (this.searchText || "").trim();
        if (termo) {
          this.onSearch();
        } else {
          this.filtradas = [...this.linhas];
          this.page = 1;
          this.ajustarPagina();
        }
        this.util.closeLoading();
      },
      () => {
        this.util.closeLoading();
        this.util.toasterShowError(
          "Atenção",
          "Erro ao carregar respostas do diário"
        );
      }
    );
  }

  onPageSizeChange(): void {
    this.page = 1;
    this.ajustarPagina();
  }

  onPageChange(): void {
    this.ajustarPagina();
  }

  private ajustarPagina(): void {
    const ultima = Math.max(1, Math.ceil(this.filtradas.length / this.pageSize));
    if (this.page > ultima) {
      this.page = ultima;
    }
  }

  onScrollDragStart(event: MouseEvent, el: HTMLElement): void {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest("a")) {
      return;
    }
    let lastX = event.clientX;
    el.classList.add("is-grabbing");

    const onMove = (ev: MouseEvent) => {
      el.scrollLeft -= ev.clientX - lastX;
      lastX = ev.clientX;
    };

    const onUp = () => {
      el.classList.remove("is-grabbing");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  /** Ordena por data de cadastro crescente (mais antiga primeiro); desempate pelo id da resposta crescente. */
  private ordenarLinhasPorDataCrescente(
    linhas: DiarioRespostaLinha[]
  ): DiarioRespostaLinha[] {
    return [...linhas].sort((a, b) => {
      const ta = a.dataCadastroOrdem;
      const tb = b.dataCadastroOrdem;
      if (ta !== tb) {
        return ta - tb;
      }
      return a.id - b.id;
    });
  }

  /**
   * Converte valor bruto da API em milissegundos UTC para ordenação.
   * Cobre ISO, Microsoft /Date(ms)/, dd/MM/yyyy HH:mm:ss e timestamp numérico.
   */
  private parseDataCadastroMs(raw: unknown): number {
    if (raw == null) {
      return 0;
    }
    if (raw instanceof Date) {
      const t = raw.getTime();
      return Number.isNaN(t) ? 0 : t;
    }
    if (typeof raw === "number" && Number.isFinite(raw)) {
      const n = raw > 1e12 ? raw : raw * 1000;
      return Number.isFinite(n) ? n : 0;
    }
    const s = String(raw).trim();
    if (!s) {
      return 0;
    }
    const msDate = /^\/Date\((-?\d+)\)\/$/.exec(s);
    if (msDate) {
      const n = Number(msDate[1]);
      return Number.isFinite(n) ? n : 0;
    }
    const br = s.match(
      /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}):(\d{2}))?$/
    );
    if (br) {
      const dd = Number(br[1]);
      const mo = Number(br[2]) - 1;
      const yyyy = Number(br[3]);
      const hh = br[4] != null ? Number(br[4]) : 0;
      const mi = br[5] != null ? Number(br[5]) : 0;
      const ss = br[6] != null ? Number(br[6]) : 0;
      return Date.UTC(yyyy, mo, dd, hh, mi, ss);
    }
    const isoTry = Date.parse(s);
    if (!Number.isNaN(isoTry)) {
      return isoTry;
    }
    return 0;
  }

  private mapLinha(item: any): DiarioRespostaLinha {
    const id =
      item.iD_DIARIO_RESPOSTAS ??
      item.ID_DIARIO_RESPOSTAS ??
      item.idDiarioRespostas;
    const dataRaw =
      item.dataCadastro ??
      item.datA_CADASTRO ??
      item.dATA_CADASTRO ??
      item.DATA_CADASTRO ??
      item.data_CADASTRO;
    const dataStr = dataRaw != null ? String(dataRaw) : "";
    const dataCadastroOrdem = this.parseDataCadastroMs(dataRaw);
    return {
      id: typeof id === "number" ? id : Number(id),
      nomeResponsavel:
        item.nomeResponsavel ??
        item.nomE_RESPONSAVEL ??
        item.NOME_RESPONSAVEL ??
        "",
      nomeCrianca: item.nomE_CRIANCA ?? item.NOME_CRIANCA ?? "",
      sexoCrianca:
        item.sexoCrianca ?? item.sexO_CRIANCA ?? item.SEXO_CRIANCA ?? "",
      tituloAtividadePrograma:
        item.titulO_ATIVIDADE_PROGRAMA ??
        item.TITULO_ATIVIDADE_PROGRAMA ??
        "",
      tituloEtapaPrograma:
        item.titulO_ETAPA_PROGRAMA ??
        item.TITULO_ETAPA_PROGRAMA ??
        "",
      textoEscolhido: item.textO_ESCOLHIDO ?? item.TEXTO_ESCOLHIDO ?? "",
      opcaoEscolhido: item.opcaO_ESCOLHIDO ?? item.OPCAO_ESCOLHIDO ?? "",
      path: item.path ?? item.PATH ?? "",
      alimento:
        item.campO_LIVRE ??
        item.CAMPO_LIVRE ??
        item.campoLivre ??
        "",
      observacao:
        item.observacaO ?? item.OBSERVACAO ?? item.observacao ?? "",
      dataCadastro: dataStr,
      dataCadastroOrdem,
    };
  }

  onSearch(): void {
    const termo = (this.searchText || "").toLowerCase().trim();
    if (!termo) {
      this.filtradas = this.ordenarLinhasPorDataCrescente([...this.linhas]);
    } else {
      this.filtradas = this.ordenarLinhasPorDataCrescente(
        this.linhas.filter((r) =>
        [
          r.nomeResponsavel,
          r.nomeCrianca,
          r.sexoCrianca,
          r.tituloAtividadePrograma,
          r.tituloEtapaPrograma,
          r.textoEscolhido,
          r.opcaoEscolhido,
          r.path,
          r.alimento,
          r.observacao,
          String(r.id),
        ]
          .filter(Boolean)
          .some((c) => String(c).toLowerCase().includes(termo))
        )
      );
    }
    this.page = 1;
    this.ajustarPagina();
  }
}
